import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Divider,
  Alert,
  Container,
  Card,
  CardContent,
  Chip,
  Grid,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PaymentIcon from "@mui/icons-material/Payment";
import PersonIcon from "@mui/icons-material/Person";
import BadgeIcon from "@mui/icons-material/Badge";
import PhoneIcon from "@mui/icons-material/Phone";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import CakeIcon from "@mui/icons-material/Cake";
import { useParams } from "react-router-dom";

const API_BASE_URL = process.env.REACT_APP_API_URL || "";

const PublicPayment = () => {
  const { shortCode } = useParams();
  const [loading, setLoading] = useState(true);
  const [paymentData, setPaymentData] = useState(null);
  const [error, setError] = useState(null);
  const [errorCode, setErrorCode] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [receiptNo, setReceiptNo] = useState(null);

  // Fetch payment link details
  const fetchPaymentDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${API_BASE_URL}/public/pay/${shortCode}`);
      const result = await response.json();

      if (result.status === 200 && result.data) {
        setPaymentData(result.data);
      } else {
        setError(result.message || "Payment link not found");
        setErrorCode(result.data?.code);
      }
    } catch (err) {
      console.error("Error fetching payment details:", err);
      setError("Failed to load payment details. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [shortCode]);

  useEffect(() => {
    if (shortCode) {
      fetchPaymentDetails();
    }
  }, [shortCode, fetchPaymentDetails]);

  const [qrData, setQrData] = useState(null);
  const [pollingActive, setPollingActive] = useState(false);

  // Handle payment - Generate UPI QR
  const handlePayment = async () => {
    try {
      setProcessing(true);
      setError(null);

      // Initiate payment order - get UPI QR string
      const initiateResponse = await fetch(`${API_BASE_URL}/public/pay/${shortCode}/initiate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      const initiateResult = await initiateResponse.json();

      if (initiateResult.status !== 200) {
        // Better error message for API failures
        let errorMsg = initiateResult.message || "Failed to initiate payment";
        if (errorMsg.includes("Unauthorized") || errorMsg.includes("401")) {
          errorMsg = "Payment gateway configuration error. Please contact PPSK support.";
        } else if (errorMsg.includes("Internal") || errorMsg.includes("500")) {
          errorMsg = "Payment service temporarily unavailable. Please try again later.";
        }
        setError(errorMsg);
        setProcessing(false);
        return;
      }

      const { qrString, refId, amount, vpaId } = initiateResult.data;

      // Set QR data for display
      setQrData({
        qrString,
        refId,
        amount,
        vpaId,
      });

      // Start polling for payment status
      setPollingActive(true);
      startPaymentPolling();
      setProcessing(false);
    } catch (err) {
      console.error("Payment error:", err);
      setError("An error occurred. Please try again.");
      setProcessing(false);
    }
  };

  // Poll for payment status
  const startPaymentPolling = () => {
    let pollCount = 0;
    const maxPolls = 60; // Poll for 5 minutes (60 * 5 seconds)

    const pollInterval = setInterval(async () => {
      pollCount++;
      
      if (pollCount >= maxPolls) {
        clearInterval(pollInterval);
        setPollingActive(false);
        setError("Payment timeout. Please try again or check your UPI app.");
        return;
      }

      try {
        const verifyResponse = await fetch(`${API_BASE_URL}/public/pay/${shortCode}/verify`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        });
        const verifyResult = await verifyResponse.json();

        if (verifyResult.status === 200) {
          // Payment successful
          clearInterval(pollInterval);
          setPollingActive(false);
          setPaymentSuccess(true);
          setReceiptNo(verifyResult.data?.receiptNo);
          setQrData(null);
        } else if (verifyResult.status !== 202) {
          // Payment failed (not pending)
          clearInterval(pollInterval);
          setPollingActive(false);
          setError(verifyResult.message || "Payment failed");
          setQrData(null);
        }
        // If status is 202, payment is pending - continue polling
      } catch (err) {
        console.error("Payment status check error:", err);
      }
    }, 5000); // Poll every 5 seconds
  };

  // Open UPI app directly
  const openUPIApp = () => {
    if (qrData?.qrString) {
      window.location.href = qrData.qrString;
    }
  };

  // Render error states
  const renderError = () => {
    let icon = <ErrorIcon sx={{ fontSize: 64, color: "error.main" }} />;
    let title = "Error";
    let message = error;

    if (errorCode === "ALREADY_PAID") {
      icon = <CheckCircleIcon sx={{ fontSize: 64, color: "success.main" }} />;
      title = "Already Paid";
      message = "This payment has already been completed successfully.";
    } else if (errorCode === "EXPIRED") {
      icon = <AccessTimeIcon sx={{ fontSize: 64, color: "warning.main" }} />;
      title = "Link Expired";
      message = "This payment link has expired. Please request a new renewal reminder.";
    } else if (errorCode === "NOT_FOUND") {
      title = "Link Not Found";
      message = "This payment link does not exist or has been removed.";
    }

    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        {icon}
        <Typography variant="h5" sx={{ mt: 2, fontWeight: 600 }}>
          {title}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
          {message}
        </Typography>
      </Box>
    );
  };

  const [downloadingReceipt, setDownloadingReceipt] = useState(false);

  // Download receipt
  const handleDownloadReceipt = async () => {
    try {
      setDownloadingReceipt(true);
      const response = await fetch(`${API_BASE_URL}/public/pay/${shortCode}/receipt`);
      const result = await response.json();

      if (result.status === 200 && result.data?.blobUrl) {
        // Decode base64 HTML and open in new window for print/save
        const htmlContent = atob(result.data.blobUrl);
        const newWindow = window.open('', '_blank');
        if (newWindow) {
          newWindow.document.write(htmlContent);
          newWindow.document.close();
        } else {
          // Fallback: create a download link
          const blob = new Blob([htmlContent], { type: 'text/html' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `PPSK_Receipt_${result.data.receiptNo || 'download'}.html`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }
      } else {
        alert(result.message || "Failed to download receipt");
      }
    } catch (err) {
      console.error("Receipt download error:", err);
      alert("Failed to download receipt. Please try again.");
    } finally {
      setDownloadingReceipt(false);
    }
  };

  // Render success state
  const renderSuccess = () => (
    <Box sx={{ textAlign: "center", py: 4 }}>
      <CheckCircleIcon sx={{ fontSize: 80, color: "success.main" }} />
      <Typography variant="h4" sx={{ mt: 2, fontWeight: 700, color: "success.main" }}>
        Payment Successful!
      </Typography>
      <Typography variant="h6" sx={{ mt: 2 }}>
        Your PPSK membership has been renewed.
      </Typography>
      {receiptNo && (
        <Box sx={{ mt: 2, p: 2, bgcolor: "#f5f5f5", borderRadius: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Receipt No
          </Typography>
          <Typography variant="h6" fontWeight={700} color="primary.main">
            {receiptNo}
          </Typography>
        </Box>
      )}
      
      {/* Download Receipt Button */}
      <Button
        variant="contained"
        color="primary"
        fullWidth
        onClick={handleDownloadReceipt}
        disabled={downloadingReceipt}
        startIcon={downloadingReceipt ? <CircularProgress size={20} color="inherit" /> : <ReceiptLongIcon />}
        sx={{ mt: 3, py: 1.5, borderRadius: 2, fontWeight: 600 }}
      >
        {downloadingReceipt ? "Generating Receipt..." : "Download Receipt"}
      </Button>

      <Alert severity="success" sx={{ mt: 3, textAlign: "left" }}>
        A confirmation message will be sent to your registered phone number.
      </Alert>

      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
        You can also view your receipt in the PPSK mobile app under Payment History.
      </Typography>
    </Box>
  );

  // UPI Apps data
  const upiApps = [
    { name: "GPay", icon: "https://upload.wikimedia.org/wikipedia/commons/thumb/f/f2/Google_Pay_Logo.svg/512px-Google_Pay_Logo.svg.png" },
    { name: "PhonePe", icon: "https://upload.wikimedia.org/wikipedia/commons/thumb/7/71/PhonePe_Logo.svg/512px-PhonePe_Logo.svg.png" },
    { name: "Paytm", icon: "https://upload.wikimedia.org/wikipedia/commons/thumb/2/24/Paytm_Logo_%28standalone%29.svg/512px-Paytm_Logo_%28standalone%29.svg.png" },
    { name: "BHIM", icon: "https://upload.wikimedia.org/wikipedia/commons/thumb/e/e1/UPI-Logo-vector.svg/512px-UPI-Logo-vector.svg.png" },
  ];

  // Build info row like mobile app
  const InfoRow = ({ label, value }) => (
    <Box sx={{ display: "flex", alignItems: "flex-start", mb: 1.5 }}>
      <Typography 
        variant="body2" 
        sx={{ 
          minWidth: 100, 
          color: "text.secondary",
          fontSize: "0.875rem"
        }}
      >
        {label}
      </Typography>
      <Typography 
        variant="body2" 
        sx={{ 
          fontWeight: 500,
          fontSize: "0.875rem",
          color: "text.primary"
        }}
      >
        {value}
      </Typography>
    </Box>
  );

  // Summary item like mobile app
  const SummaryItem = ({ label, value, isBold = false }) => (
    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
      <Typography 
        variant="body2" 
        sx={{ 
          color: isBold ? "text.primary" : "text.secondary",
          fontWeight: isBold ? 600 : 400,
          fontSize: isBold ? "1rem" : "0.875rem"
        }}
      >
        {label}
      </Typography>
      <Typography 
        variant="body2" 
        sx={{ 
          fontWeight: isBold ? 700 : 500,
          fontSize: isBold ? "1.1rem" : "0.875rem",
          color: isBold ? "primary.main" : "text.primary"
        }}
      >
        {value}
      </Typography>
    </Box>
  );

  // Render payment form - Combined Summary + Payment like mobile app
  const renderPaymentForm = () => (
    <Box>
      {/* Header - App Style */}
      <Box sx={{ 
        display: "flex", 
        alignItems: "center", 
        mb: 3,
        pb: 2,
        borderBottom: "1px solid",
        borderColor: "divider"
      }}>
        <img 
          src="/logo192.png" 
          alt="PPSK" 
          style={{ width: 48, height: 48, marginRight: 12, borderRadius: 8 }}
          onError={(e) => { e.target.style.display = 'none'; }}
        />
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "primary.main", lineHeight: 1.2 }}>
            PPSK Renewal
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Membership Payment
          </Typography>
        </Box>
        <Chip 
          label={paymentData?.timeRemaining} 
          size="small" 
          icon={<AccessTimeIcon sx={{ fontSize: 14 }} />}
          sx={{ ml: "auto", bgcolor: "#fff3e0", color: "#e65100" }}
        />
      </Box>

      {/* Member Details Card - Like Mobile App */}
      <Card 
        elevation={0} 
        sx={{ 
          mb: 2, 
          border: "1px solid",
          borderColor: "grey.200",
          borderRadius: 3
        }}
      >
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <PersonIcon sx={{ fontSize: 18, color: "primary.main", mr: 1 }} />
            <Typography variant="subtitle2" fontWeight={600}>
              Member Details
            </Typography>
          </Box>
          <InfoRow label="Name" value={paymentData?.memberName || "-"} />
          <InfoRow label="PPSK ID" value={paymentData?.ppskId || "-"} />
          <InfoRow label="Phone" value={paymentData?.maskedPhone || "-"} />
          {paymentData?.memberAge && (
            <InfoRow label="Age" value={`${paymentData.memberAge} Years`} />
          )}
        </CardContent>
      </Card>

      {/* Policy Duration Card - Like Mobile App */}
      <Card 
        elevation={0} 
        sx={{ 
          mb: 2, 
          border: "1px solid",
          borderColor: "grey.200",
          borderRadius: 3
        }}
      >
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <CalendarTodayIcon sx={{ fontSize: 18, color: "primary.main", mr: 1 }} />
            <Typography variant="subtitle2" fontWeight={600}>
              Policy Duration (પોલિસી અવધિ)
            </Typography>
          </Box>
          <InfoRow label="Start Date" value={new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} />
          <InfoRow 
            label="Expiry Date" 
            value={new Date(Date.now() + 365*24*60*60*1000).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} 
          />
          <Box sx={{ mt: 1.5, pt: 1.5, borderTop: "1px dashed", borderColor: "grey.300" }}>
            <Chip 
              label="1 Year Coverage" 
              size="small" 
              sx={{ bgcolor: "#e8f5e9", color: "#2e7d32", fontWeight: 500 }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Order Summary Card - Like Mobile App */}
      <Card 
        elevation={0} 
        sx={{ 
          mb: 2, 
          border: "1px solid",
          borderColor: "grey.200",
          borderRadius: 3
        }}
      >
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <ReceiptLongIcon sx={{ fontSize: 18, color: "primary.main", mr: 1 }} />
            <Typography variant="subtitle2" fontWeight={600}>
              Order Summary
            </Typography>
          </Box>
          <SummaryItem 
            label="Sahaynidhi (સહાયનિધિ ખર્ચ)" 
            value={`₹${(paymentData?.policyAmount || (paymentData?.amount - 100))?.toLocaleString('en-IN')}`} 
          />
          <SummaryItem 
            label="Management (મેનેજમેન્ટ ખર્ચ)" 
            value={`₹${paymentData?.managementCharge || 100}`} 
          />
          <Divider sx={{ my: 1.5 }} />
          <SummaryItem 
            label="Grand Total (કુલ રકમ)" 
            value={`₹${paymentData?.amount?.toLocaleString('en-IN')}`} 
            isBold 
          />
        </CardContent>
      </Card>

      {/* Error message */}
      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* QR Code Section - Show when payment initiated */}
      {qrData ? (
        <Card 
          elevation={0} 
          sx={{ 
            mb: 2, 
            border: "1px solid",
            borderColor: "primary.main",
            borderRadius: 3,
            background: "linear-gradient(135deg, rgba(25,118,210,0.03) 0%, white 100%)"
          }}
        >
          <CardContent sx={{ p: 2, textAlign: "center" }}>
            {/* Scan & Pay Header */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mb: 2 }}>
              <QrCodeScannerIcon sx={{ fontSize: 18, color: "primary.main", mr: 1 }} />
              <Typography variant="subtitle2" fontWeight={600}>
                Scan & Pay
              </Typography>
            </Box>
            
            {/* QR Code */}
            <Box 
              sx={{ 
                display: "inline-block",
                p: 1.5, 
                bgcolor: "white", 
                borderRadius: 2,
                border: "1px solid",
                borderColor: "grey.200",
                mb: 2
              }}
            >
              <img 
                src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(qrData.qrString)}`}
                alt="UPI QR Code"
                style={{ width: 180, height: 180, display: "block" }}
              />
            </Box>

            {/* UPI App Icons */}
            <Box sx={{ display: "flex", justifyContent: "center", gap: 1, mb: 2 }}>
              {upiApps.map((app) => (
                <Box 
                  key={app.name}
                  sx={{ 
                    width: 32, 
                    height: 32, 
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "grey.200",
                    overflow: "hidden",
                    bgcolor: "white"
                  }}
                >
                  <img 
                    src={app.icon} 
                    alt={app.name} 
                    style={{ width: "100%", height: "100%", objectFit: "contain", padding: 4 }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </Box>
              ))}
            </Box>

            <Typography variant="body2" color="text.secondary" gutterBottom>
              UPI ID: <strong>{qrData.vpaId}</strong>
            </Typography>
            <Typography variant="h5" color="primary.main" fontWeight={700} sx={{ mb: 2 }}>
              ₹{qrData.amount?.toLocaleString('en-IN')}
            </Typography>

            {/* Open UPI App Button */}
            <Button
              variant="contained"
              fullWidth
              onClick={openUPIApp}
              startIcon={<TouchAppIcon />}
              sx={{ 
                py: 1.5, 
                borderRadius: 2,
                fontWeight: 600,
                mb: 1
              }}
            >
              Open UPI App to Pay
            </Button>

            {pollingActive && (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, mt: 2 }}>
                <CircularProgress size={16} />
                <Typography variant="caption" color="text.secondary">
                  Waiting for payment confirmation...
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      ) : (
        /* Pay Section - Before QR generated */
        <Card 
          elevation={0} 
          sx={{ 
            mb: 2, 
            border: "1px solid",
            borderColor: "grey.200",
            borderRadius: 3
          }}
        >
          <CardContent sx={{ p: 2 }}>
            {/* Select UPI App Header */}
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <TouchAppIcon sx={{ fontSize: 18, color: "primary.main", mr: 1 }} />
              <Typography variant="subtitle2" fontWeight={600}>
                Pay Using UPI
              </Typography>
            </Box>

            {/* UPI App Icons - Horizontal */}
            <Box sx={{ display: "flex", gap: 1.5, mb: 3, justifyContent: "center", flexWrap: "wrap" }}>
              {upiApps.map((app) => (
                <Box 
                  key={app.name}
                  sx={{ 
                    textAlign: "center",
                    p: 1.5,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "grey.200",
                    minWidth: 70,
                    cursor: "pointer",
                    transition: "all 0.2s",
                    "&:hover": {
                      borderColor: "primary.main",
                      bgcolor: "primary.50"
                    }
                  }}
                  onClick={handlePayment}
                >
                  <Box 
                    sx={{ 
                      width: 40, 
                      height: 40, 
                      mx: "auto",
                      mb: 0.5,
                      borderRadius: 1,
                      overflow: "hidden",
                      bgcolor: "#f5f5f5",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}
                  >
                    <img 
                      src={app.icon} 
                      alt={app.name} 
                      style={{ width: 32, height: 32, objectFit: "contain" }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </Box>
                  <Typography variant="caption" sx={{ fontSize: 10 }}>
                    {app.name}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Pay Button */}
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={handlePayment}
              disabled={processing}
              startIcon={processing ? <CircularProgress size={20} color="inherit" /> : <QrCodeScannerIcon />}
              sx={{ 
                py: 1.5, 
                fontSize: "1rem",
                fontWeight: 600,
                borderRadius: 2,
                background: "linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)",
              }}
            >
              {processing ? "Generating QR..." : "Generate QR to Pay"}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Instructions */}
      <Box sx={{ 
        p: 2, 
        bgcolor: "#f8f9fa", 
        borderRadius: 2,
        mb: 2
      }}>
        <Typography variant="caption" fontWeight={600} sx={{ display: "block", mb: 1 }}>
          📱 How to Pay:
        </Typography>
        <Typography variant="caption" color="text.secondary" component="div">
          1. Click "Generate QR to Pay" button<br/>
          2. Scan QR with any UPI app (GPay, PhonePe, Paytm)<br/>
          3. Complete payment in your UPI app<br/>
          4. Wait for confirmation on this page
        </Typography>
      </Box>

      {/* Security note */}
      <Typography 
        variant="caption" 
        color="text.secondary" 
        sx={{ display: "block", textAlign: "center" }}
      >
        🔒 Secured by ICICI Bank UPI. Your payment is safe.
      </Typography>
    </Box>
  );

  return (
    <Box 
      sx={{ 
        minHeight: "100vh", 
        bgcolor: "#f5f5f5",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Container maxWidth="sm">
        <Paper 
          elevation={3} 
          sx={{ 
            p: { xs: 3, sm: 4 }, 
            borderRadius: 3,
          }}
        >
          {loading ? (
            <Box sx={{ textAlign: "center", py: 6 }}>
              <CircularProgress size={48} />
              <Typography variant="body1" sx={{ mt: 2 }}>
                Loading payment details...
              </Typography>
            </Box>
          ) : paymentSuccess ? (
            renderSuccess()
          ) : error && !paymentData ? (
            renderError()
          ) : paymentData ? (
            renderPaymentForm()
          ) : null}
        </Paper>

        {/* Footer */}
        <Typography 
          variant="caption" 
          color="text.secondary" 
          sx={{ display: "block", textAlign: "center", mt: 3 }}
        >
          © {new Date().getFullYear()} PPSK. All rights reserved.
        </Typography>
      </Container>
    </Box>
  );
};

export default PublicPayment;
